import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { CartItem, CartState, CartAction, Product } from '../types';

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, size?: string, quantity?: number) => void;
  removeFromCart: (id: string | number) => void;
  updateQuantity: (id: string | number, quantity: number) => void;
  clearCart: () => void;
  updateCartItemFromProduct: (updatedProduct: Partial<Product>) => void;
  removeCartItemsForProduct: (productId: string | number) => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getTotalSavings: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'fusion_mart_cart';

// Helper to normalize IDs for comparison (handle string/number mismatch)
const normalizeId = (id: string | number): string => String(id);

// Helper to normalize size strings
const normalizeSize = (size: string): string => size.trim();

const saveCartToStorage = (cartItems: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

const loadCartFromStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      // Normalize IDs and sizes for consistent comparison
      const payloadId = normalizeId(action.payload.id);
      const payloadSize = normalizeSize(action.payload.size);
      
      const existingItem = state.items.find(item => 
        normalizeId(item.id) === payloadId && normalizeSize(item.size) === payloadSize
      );
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item => {
            if (normalizeId(item.id) === payloadId && normalizeSize(item.size) === payloadSize) {
              return { ...item, quantity: item.quantity + action.payload.quantity };
            }
            return item;
          })
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }
    }
      
    case 'REMOVE_FROM_CART': {
      const targetId = normalizeId(action.payload);
      return {
        ...state,
        items: state.items.filter(item => normalizeId(item.id) !== targetId)
      };
    }
      
    case 'UPDATE_QUANTITY': {
      const targetId = normalizeId(action.payload.id);
      return {
        ...state,
        items: state.items.map(item =>
          normalizeId(item.id) === targetId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };
    }
      
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
      
    case 'UPDATE_CART_ITEM_FROM_PRODUCT': {
      const targetId = normalizeId(action.payload.id!);
      return {
        ...state,
        items: state.items.map(item =>
          normalizeId(item.id) === targetId
            ? {
                ...item,
                name: action.payload.name ?? item.name,
                price: action.payload.price ?? item.price,
                originalPrice: action.payload.originalPrice ?? item.originalPrice,
                image: action.payload.image ?? item.image
              }
            : item
        )
      };
    }
      
    case 'REMOVE_CART_ITEMS_FOR_PRODUCT': {
      const targetId = normalizeId(action.payload.id);
      return {
        ...state,
        items: state.items.filter(item => normalizeId(item.id) !== targetId)
      };
    }
      
    case 'CLEANUP_DELETED_PRODUCTS':
      return {
        ...state,
        items: action.payload
      };
      
    default:
      return state;
  }
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: loadCartFromStorage()
  });

  const updateCartItemFromProduct = useCallback((updatedProduct: Partial<Product>): void => {
    dispatch({
      type: 'UPDATE_CART_ITEM_FROM_PRODUCT',
      payload: updatedProduct
    });
  }, []);

  const removeCartItemsForProduct = useCallback((productId: string | number): void => {
    dispatch({
      type: 'REMOVE_CART_ITEMS_FOR_PRODUCT',
      payload: { id: productId }
    });
  }, []);

  // Cleanup deleted products - only when productDeleted event is fired
  // Don't run aggressive cleanup on mount as products may be in Supabase, not localStorage

  useEffect(() => {
    saveCartToStorage(state.items);
  }, [state.items]);

  useEffect(() => {
    const handleProductUpdate = (event: CustomEvent<Partial<Product>>): void => {
      const updatedProduct = event.detail;
      updateCartItemFromProduct(updatedProduct);
    };

    const handleProductDeletion = (event: CustomEvent<{ id: string | number }>): void => {
      const { id } = event.detail;
      removeCartItemsForProduct(id);
    };

    window.addEventListener('productUpdated', handleProductUpdate as EventListener);
    window.addEventListener('productDeleted', handleProductDeletion as EventListener);
    
    return () => {
      window.removeEventListener('productUpdated', handleProductUpdate as EventListener);
      window.removeEventListener('productDeleted', handleProductDeletion as EventListener);
    };
  }, [updateCartItemFromProduct, removeCartItemsForProduct]);

  const addToCart = (product: Product, size: string = 'M', quantity: number = 1): void => {
    if (product.available === false) {
      return;
    }
    
    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: (product.images && product.images.length > 0 ? product.images[0] : product.image),
        size: normalizeSize(size),
        quantity: quantity,
        description: product.description
      }
    });
  };

  const removeFromCart = (id: string | number): void => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: id
    });
  };

  const updateQuantity = (id: string | number, quantity: number): void => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id, quantity }
    });
  };

  const clearCart = (): void => {
    dispatch({
      type: 'CLEAR_CART'
    });
  };

  const getTotalItems = (): number => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = (): number => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalSavings = (): number => {
    return state.items.reduce((total, item) => {
      if (item.originalPrice) {
        return total + ((item.originalPrice - item.price) * item.quantity);
      }
      return total;
    }, 0);
  };

  const value: CartContextType = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    updateCartItemFromProduct,
    removeCartItemsForProduct,
    getTotalItems,
    getTotalPrice,
    getTotalSavings
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
